import * as React from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  Button,
  Input,
  Modal,
  ModalContent,
  ModalHeader,
  ModalTitle,
  ModalBody,
  ModalFooter,
} from '@insolech/ui';
import { orderMutations, orderQueries, type OrderDetail } from '@insolech/sdk';
import { supabase } from '@/lib/supabase';

/**
 * EditOrderLinePriceDialog
 *
 * Admin-only modal opened from the pencil-icon affordance next to a
 * line's price on the order detail page. Patches
 * `order_lines.unit_price_aed`, then triggers a client-side recompute of
 * the parent `orders.subtotal_aed / tax_aed / total_aed` (the
 * `line_total_aed` generated column auto-updates from the new unit
 * price; the orders header has no DB trigger today, so the SDK helper
 * re-sums and PATCHes it in the same call).
 *
 * Works at any order status (admins must be able to fix a wrong price
 * even on delivered orders). RLS already permits admin UPDATE on both
 * order_lines and orders at any status, so no migration is needed.
 *
 * Modal scaffolding mirrors EditPrescriptionDialog so the visual
 * language stays consistent.
 */

type Line = OrderDetail['lines'][number];

interface Props {
  open: boolean;
  order: OrderDetail;
  line: Line;
  onClose: () => void;
}

export function EditOrderLinePriceDialog({ open, order, line, onClose }: Props) {
  const qc = useQueryClient();

  // Hydrate the input from the current price each time the dialog opens.
  const [priceStr, setPriceStr] = React.useState<string>(() => line.unit_price_aed.toFixed(2));
  const [saving, setSaving] = React.useState(false);
  const [saveError, setSaveError] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (!open) return;
    setPriceStr(line.unit_price_aed.toFixed(2));
    setSaveError(null);
    setSaving(false);
  }, [open, line.id, line.unit_price_aed]);

  const parsed = Number.parseFloat(priceStr);
  const isValid = Number.isFinite(parsed) && parsed >= 0;
  const newLineTotal = isValid ? parsed * line.quantity : null;

  const onSave = async () => {
    setSaveError(null);
    if (!isValid) {
      setSaveError('Please enter a non-negative price.');
      return;
    }
    setSaving(true);
    try {
      await orderMutations.updateOrderLinePrice(supabase, {
        orderId: order.id,
        orderLineId: line.id,
        unit_price_aed: parsed,
      });
      // Refresh detail + the orders list root so the tracker total
      // updates without a manual reload. History is unchanged (no
      // status transition), so we skip that key.
      await Promise.all([
        qc.invalidateQueries({ queryKey: orderQueries.ordersKeys.detail(order.id) }),
        qc.invalidateQueries({ queryKey: orderQueries.ordersKeys.all }),
      ]);
      onClose();
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      setSaveError(msg || 'Unknown error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal open={open} onOpenChange={(v) => !v && !saving && onClose()}>
      <ModalContent size="sm">
        <ModalHeader>
          <ModalTitle>
            Edit line price · {order.order_number} · Line {line.line_number}
          </ModalTitle>
        </ModalHeader>
        <ModalBody className="space-y-4">
          {saveError ? (
            <Alert tone="error" title="Could not save price">
              {saveError}
            </Alert>
          ) : null}

          {/* Branch on prices_include_vat, NOT is_direct_billed. They are
              deliberately decoupled, so telling the admin "VAT is backed out"
              on an order whose basis is exclusive would make them type the
              gross figure and over-charge by 5% - the exact bug this release
              fixes, re-entered through its own instructions. */}
          <Alert tone="info" title="Admin price override">
            {order.prices_include_vat
              ? 'The price you enter is the amount the patient pays: 5% VAT is backed out of it, not added on top. Changing it re-computes the order net, VAT and total.'
              : 'The price you enter is exclusive of VAT: 5% is added on top. Changing it re-computes the order subtotal, VAT (5%) and total.'}{' '}
            Agent commissions already booked for this order are not re-accrued.
          </Alert>

          <div className="space-y-1.5">
            <label
              htmlFor="edit-line-price-input"
              className="text-xs font-medium uppercase tracking-wide text-text-muted"
            >
              Unit price (AED)
            </label>
            <Input
              id="edit-line-price-input"
              type="number"
              inputMode="decimal"
              min={0}
              step="0.01"
              value={priceStr}
              onChange={(e) => setPriceStr(e.target.value)}
              error={priceStr.trim().length > 0 && !isValid}
              autoFocus
            />
            <p className="text-xs text-text-muted">
              Quantity: {line.quantity}
              {newLineTotal != null ? (
                <>
                  {' · New line total: '}
                  <span className="tabular font-medium text-text-primary">
                    AED {newLineTotal.toFixed(2)}
                  </span>
                </>
              ) : null}
            </p>
          </div>
        </ModalBody>
        <ModalFooter className="flex items-center justify-between gap-3">
          <Button variant="secondary" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={onSave}
            disabled={saving || !isValid}
          >
            {saving ? 'Saving...' : 'Save price'}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
